/**
 * SnakeBoardIntro — staggered "stage building" entry animation.
 *
 * When a Snake board is built (game start, restart, or board transition),
 * every visual element on the field (obstacles, stations, food, exit gate,
 * house, door…) is registered with this animator. Each node:
 *
 *   • starts invisible (alpha=0, scale=0) and offset above its final spot
 *   • staggers in based on its position (top-left first → bottom-right last)
 *   • drops into place with an easeOutCubic translation
 *   • pops in with an easeOutBack scale curve and a quick alpha fade
 *
 * The animator is purely additive — it does NOT take ownership of the
 * nodes. If a node is destroyed mid-animation (e.g. scene tear-down),
 * call {@link clear} or {@link finish} to drop references.
 *
 * Why a custom mini-tweener instead of pulling in a library: the rest of
 * the codebase already drives all motion from PIXI's per-frame ticker
 * (see `SnakeScene.update`) and does interpolation by hand. Keeping the
 * intro in the same idiom avoids a vendor dependency and means the
 * animation is naturally paused whenever the scene's update is paused.
 */
export class SnakeBoardIntro {
    /** @type {Array<IntroItem>} */
    _items = [];
    _elapsedMs = 0;
    _done = true;

    /** Default per-item animation duration (ms). */
    static DURATION_MS = 420;
    /** Default delay between successive items (ms). Smaller = more overlap. */
    static STAGGER_MS = 32;
    /** Default vertical drop distance (px) — items fall from this far up. */
    static DROP_PX = 70;

    /**
     * Reset everything and prepare to register a new batch of nodes.
     * Snaps any in-flight items to their target before clearing so we
     * never leave a previously animated obstacle stuck at scale=0.
     */
    reset() {
        this.finish();
        this._items.length = 0;
        this._elapsedMs = 0;
        this._done = true;
    }

    /**
     * Register a Pixi Container/Sprite for the cascade.
     *
     * @param {object}  node               the display object to animate
     * @param {number}  centerX            its target centre X in world space
     * @param {number}  centerY            its target centre Y in world space
     * @param {number}  [delayMs=0]        ms to wait before this item starts
     * @param {object}  [opts]
     * @param {number}  [opts.durationMs]  override default per-item duration
     * @param {number}  [opts.dropPx]      override default drop distance
     */
    addNode(node, centerX, centerY, delayMs = 0, opts = {}) {
        if (!node) return;
        const durationMs = opts.durationMs ?? SnakeBoardIntro.DURATION_MS;
        const dropPx = opts.dropPx ?? SnakeBoardIntro.DROP_PX;

        // Re-pivot the node around its visual centre so scale interpolates
        // from the middle of the artwork instead of from the world origin.
        // Compensating x/y keeps the rendered position unchanged at scale=1.
        node.pivot.set(centerX, centerY);
        node.x = centerX;
        node.y = centerY - dropPx;
        node.scale.set(0, 0);
        node.alpha = 0;

        this._items.push({
            node,
            cx: centerX,
            cy: centerY,
            startY: centerY - dropPx,
            delayMs,
            durationMs,
            done: false,
        });
        this._done = false;
    }

    /**
     * Returns true while any registered node is still animating
     * (or waiting for its stagger delay).
     */
    isPlaying() {
        return !this._done;
    }

    /** Advance every active animation by `deltaMs` milliseconds. */
    update(deltaMs) {
        if (this._done || this._items.length === 0) return;
        this._elapsedMs += deltaMs;

        let anyActive = false;
        for (const it of this._items) {
            if (it.done) continue;
            if (it.node?.destroyed) { it.done = true; continue; }

            const t = (this._elapsedMs - it.delayMs) / it.durationMs;
            if (t < 0) { anyActive = true; continue; }

            if (t >= 1) {
                it.node.y = it.cy;
                it.node.scale.set(1, 1);
                it.node.alpha = 1;
                it.done = true;
                continue;
            }

            anyActive = true;
            const eScale = easeOutBack(t);
            const eDrop  = easeOutCubic(t);
            it.node.y     = it.startY + (it.cy - it.startY) * eDrop;
            it.node.scale.set(eScale, eScale);
            it.node.alpha = Math.min(1, t * 2.2);
        }

        if (!anyActive) {
            this._done = true;
        }
    }

    /** Snap every pending node to its final transform immediately. */
    finish() {
        for (const it of this._items) {
            if (it.done || !it.node || it.node.destroyed) {
                it.done = true;
                continue;
            }
            it.node.y = it.cy;
            it.node.scale.set(1, 1);
            it.node.alpha = 1;
            it.done = true;
        }
        this._done = true;
    }

    /** Forget all references without touching node transforms. */
    clear() {
        this._items.length = 0;
        this._elapsedMs = 0;
        this._done = true;
    }
}

/* ── Easing helpers ──────────────────────────────────────── */

function easeOutBack(t) {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    const c1 = 1.70158;
    const c3 = c1 + 1;
    const tm = t - 1;
    return 1 + c3 * tm * tm * tm + c1 * tm * tm;
}

function easeOutCubic(t) {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    const u = 1 - t;
    return 1 - u * u * u;
}

/* ── Type hints (JSDoc only — purely informative) ─────────
 * @typedef {{
 *   node:        any,
 *   cx:          number,
 *   cy:          number,
 *   startY:      number,
 *   delayMs:     number,
 *   durationMs:  number,
 *   done:        boolean
 * }} IntroItem
 */
